import React from 'react'
import { Bar, BarChart, CartesianGrid, LabelList, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { dailyTicks, formatDecimal, formatNumber } from '../../../utilities/dashboard'
import ChartTooltip, { BarLabel } from './ChartTooltip'
import useChartColors from './useChartColors'

// Texto dos eixos, na cor do tema
export const axisTick = (colors) => ({ fontSize: 11, fill: colors.tick })
// Barra sob o mouse (ou o foco do teclado) clareia um pouco
export const ACTIVE_BAR = { fillOpacity: 0.8 }
// Descrição (<desc>) dos gráficos com accessibilityLayer, que viram 'application' focável; o nome vem do title
export const CHART_DESC = 'Use as setas para navegar; Ver tabela mostra os números'

// Rótulo da linha da média, à direita do gráfico: "média" e o valor
const AverageLabel = ({ viewBox, average }) => {
  const { chart } = useChartColors()
  if (!viewBox) return null
  const x = viewBox.x + viewBox.width + 6
  return (
    <g>
      <text x={x} y={viewBox.y - 2} fill={chart.average} fontSize={11} fontWeight={600}>média</text>
      <text x={x} y={viewBox.y + 11} fill={chart.average} fontSize={11} fontWeight={600}>{formatDecimal(average)}</text>
    </g>
  )
}

function tooltipFor(row, colors) {
  const monthly = row.scale === 'month'
  if (row.future) return { title: row.title, notes: [monthly ? 'mês que ainda não chegou' : 'dia que ainda não chegou'] }
  const notes = []
  if (row.today) notes.push('hoje, até agora (fora da média)')
  if (row.partial && monthly) notes.push('mês incompleto: cortado pelo período ou ainda em andamento')

  // Por mês, o backend não manda pacientes (pacientes de dias diferentes não se somam)
  const rows = [{ color: colors.primary, value: formatNumber(row.total), label: 'atendimentos' }]
  if (!monthly) rows.push({ value: formatNumber(row.pacientes), label: 'pacientes' })
  return { title: row.title, rows, notes }
}

// Atendimentos por dia (ou por mês, acima de 62 dias), com a linha da média e o fundo cinza no fim de semana
const DailyChart = ({ rows, average, narrow, title }) => {
  const { chart: colors } = useChartColors()
  const monthly = rows[0]?.scale === 'month'
  const labels = Object.fromEntries(rows.map((row) => [row.date, row.label]))
  const showAverage = !monthly && (average !== null) && (average !== undefined)
  // Mês que ainda não chegou (total null) não tem barra: sem o rótulo
  const partialRows = rows.map((row) => ({ ...row, tag: (monthly && row.partial && (row.total !== null)) ? 'parcial' : null }))

  return (
    <ResponsiveContainer width='100%' height={260}>
      <BarChart data={partialRows} margin={{ top: 18, right: showAverage ? 52 : 8, bottom: 0, left: -10 }} accessibilityLayer title={title} desc={CHART_DESC}>
        {rows.filter((row) => row.weekend).map((row) => (
          <ReferenceArea key={row.date} x1={row.date} x2={row.date} fill={colors.shade} fillOpacity={1} />
        ))}
        <CartesianGrid vertical={false} stroke={colors.grid} />
        <XAxis
          dataKey='date'
          tickFormatter={(date) => labels[date] ?? ''}
          ticks={dailyTicks(rows, narrow)}
          interval={0}
          tickLine={false}
          axisLine={{ stroke: colors.axis }}
          tick={axisTick(colors)}
        />
        <YAxis allowDecimals={false} width={44} tickLine={false} axisLine={false} tick={axisTick(colors)} />
        {/* filterNull false: sem ele o recharts tira o total null do payload, e o dia (ou mês) que ainda não chegou fica sem tooltip */}
        <Tooltip cursor={{ fill: colors.cursor }} content={<ChartTooltip render={tooltipFor} />} filterNull={false} isAnimationActive={false} />
        <Bar dataKey='total' name='Atendimentos' fill={colors.primary} radius={[4, 4, 0, 0]} maxBarSize={24} activeBar={ACTIVE_BAR} isAnimationActive={false}>
          {monthly ? <LabelList dataKey='tag' content={<BarLabel fontSize={10} fontWeight={600} />} /> : ''}
        </Bar>
        {showAverage ?
          <ReferenceLine y={average} stroke={colors.average} strokeWidth={1.5} ifOverflow='extendDomain' label={<AverageLabel average={average} />} />
          : ''}
      </BarChart>
    </ResponsiveContainer>
  )
}

export default DailyChart
